import type { TenantEntry } from '@linksoft/tenant-registry';
import { toCustomerConfigState } from '~~/layers/config/shared/utils/tenant-config';

/**
 * Fetches `/api-override.json` from the public folder at runtime
 * and overrides the baked-in apiOrigin if the resolved config allows it.
 *
 * Expected JSON format:
 * {
 *   "apiOrigin": "https://new-api.example.com"
 * }
 *
 * If the file doesn't exist or the tenant's resolved settings do not declare
 * `allowApiOriginOverride: true`, this plugin is a no-op.
 */
export default defineNuxtPlugin({
	name: 'api-override',
	enforce: 'pre',
	async setup() {
		// The mutated object is appConfig.tenant's environment. Every later
		// apiOrigin read re-projects that entry, so the override reaches all of them.
		const entry = useAppConfig().tenant as TenantEntry | undefined;
		if (!entry) return;

		const environment = entry.customer.environments?.[entry.deployment.environment];
		if (!environment) return;
		if (!toCustomerConfigState(entry).allowApiOriginOverride) return;

		try {
			const override = await $fetch<{ apiOrigin?: string }>('/api-override.json', {
				responseType: 'json',
			});

			if (override?.apiOrigin) {
				frogger.info('apiOrigin overridden at runtime', {
					fromOrigin: environment.endpoints.api,
					toOrigin: override.apiOrigin,
				});
				environment.endpoints.api = override.apiOrigin;
			}
		} catch (error) {
			// On IIS SPA-fallback a missing /api-override.json returns 200 index.html (a JSON
			// parse error) rather than a clean 404, so absent-vs-invalid is not reliably
			// distinguishable; an absent override is the expected common case, hence debug.
			frogger.debug('api-override.json unavailable or unreadable', error);
		}
	},
});
